"use client";
import { IAccountAssets, IBlocks, ITheme, IUserData } from "@/types";
import React from "react";
import { BlockUpdateFunction } from ".";
import AdminPopover from "../../content/AdminPopover";
import { shadow } from "@/lib/utils";
import { Separator } from "@/components/ui/separator";
import BlockToggle from "./tabs/toggle";
import RichTextBlockOptions from "./tabs/richtextblock/options";
import RichTextBlockCustomization from "./tabs/richtextblock/customization";
import dynamic from "next/dynamic";
import BlockMetadata from "./tabs/metadata";
const EditorComponent = dynamic(() => import("@/components/admin/BlockNote"), {
    ssr: false,
});
interface Props {
    admin?: boolean;
    user: IUserData;
    block: IBlocks;
    update: BlockUpdateFunction;
    style: any;
    theme: ITheme;
    newBlock?: string | null;
    assets: IAccountAssets[] | undefined;
    impersonate?: IUserData | null;
}
export default function BlockRichText({
    admin,
    user,
    block,
    update,
    style,
    theme,
    newBlock,
    assets,
    impersonate,
}: Props) {
    const [isExpanded, setIsExpanded] = React.useState(false);
    const [blockUpdate, setBlockUpdate] = React.useState(block);
    React.useEffect(() => {
        if (newBlock === block.id) {
            handleExpand();
        }
        return () => {
            window.removeEventListener("closeBlock", handleClose);
        };
    }, [newBlock, block.id]);
    React.useEffect(() => {
        window.addEventListener("closeBlock", {
            handleEvent: (event: CustomEvent) => {
                if (event.detail.id != block.id) {
                    if (isExpanded) {
                        setIsExpanded(false);
                    }
                }
            },
        });
    });
    React.useEffect(() => {
        if (block != blockUpdate) {
            setBlockUpdate(block);
        }
    }, [block]);
    const handleClose = () => {
        setIsExpanded(false);
        window.dispatchEvent(new CustomEvent("collapseBlock"));
    };
    function handleUpdate(value: IBlocks) {
        if (value == null) {
            update(blockUpdate.id, value, true);
            return;
        }
        setBlockUpdate({
            ...blockUpdate,
            ...value,
        });
        if (block == value) return;
        update(blockUpdate.id, value);
    }
    const event = new CustomEvent("expandBlock", {
        detail: {
            id: blockUpdate.id,
            title: "Rich Text",
            titleOptions: (
                <BlockToggle
                    block={blockUpdate}
                    blockUpdate={setBlockUpdate}
                    update={handleUpdate}
                />
            ),
            content: (
                <div className="flex flex-col gap-3 pb-3" key={block.id}>
                    <RichTextBlockOptions
                        block={blockUpdate}
                        blockUpdate={setBlockUpdate}
                        update={handleUpdate}
                        assets={assets}
                        account_id={user.id}
                        impersonate={
                            impersonate
                                ? {
                                      id: impersonate.id,
                                  }
                                : null
                        }
                    />
                    <RichTextBlockCustomization
                        block={blockUpdate}
                        blockUpdate={setBlockUpdate}
                        update={handleUpdate}
                    />
                    <BlockMetadata block={blockUpdate} update={handleUpdate} />
                </div>
            ),
        },
    });
    const handleExpand = () => {
        window.dispatchEvent(
            new CustomEvent("closeBlock", {
                detail: {
                    id: block.id,
                },
            })
        );
        setIsExpanded(true);
        window.dispatchEvent(event);
    };
    React.useEffect(() => {
        if (isExpanded) {
            window.dispatchEvent(event);
        }
    }, [blockUpdate]);
    return (
        <AdminPopover
            admin={admin}
            className={blockUpdate.show ? "" : "opacity-30"}
            onClick={handleExpand}
            title={"Rich Text Block"}
            isOpen={isExpanded}
            onClose={handleClose}
            key={block.id}
        >
            <div
                key={block.id}
                style={style}
                className={`${shadow(theme.button_shadow)}
                ${theme?.button_rounded == "rounded-full" ? "rounded-2xl" : theme?.button_rounded}
                richText 
                w-full 
                px-3 py-3 text-xl`}
            >
                <EditorComponent
                    value={blockUpdate.options.content}
                    id={block.id}
                    key={block.id}
                    readOnly
                    updateContent={blockUpdate.options.content}
                />
            </div>
        </AdminPopover>
    );
}
